import { inject, ChangeDetectionStrategy, signal, Component, input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { TableModule } from 'primeng/table';
import { CardContent } from '@/shared/components/card-content/card-content';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { TelemedicinaFeatureService } from './telemedicina.service';
import { MedicoTelemedicinaHistoricoResponse, MedicoTelemedicinaResponse, TipoTelemedicinaResponse } from '@/core/api/sicom-api/registro/medico/telemedicina/telemedicina.model';
import { TelemedicinaDialogFormData, TelemedicinaEditarDialogComponent } from './dialogs/editar-telemedicina/editar-telemedicina';
import { EncerrarTelemedicinaDialogComponent } from './dialogs/encerrar-telemedicina/encerrar-telemedicina';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { MenuItem } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface TelemedicinaRow {
    tipo: string;
    dataInicio?: string;
    dataFim?: string;
}

interface TelemedicinaFormData extends TelemedicinaDialogFormData {}

@Component({
    selector: 'app-telemedicina',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ToolbarModule,
        TableModule,
        CardContent,
        InputTextModule,
        DialogModule,
        DatePickerModule,
        EncerrarTelemedicinaDialogComponent,
        TelemedicinaEditarDialogComponent,
        TabContainer,
        TabContent,
        CustomTableComponent,
        TagModule,
        BotaoAcoes,
        CanDirective
    ],
    templateUrl: './telemedicina.html',
    styleUrl: './telemedicina.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de telemedicina do médico (ativas/histórico). */
export class TelemedicinaComponent implements OnInit, OnChanges {
    private readonly logger = inject(LoggerService);

    pessoa = input<Pessoa>();
    protected readonly roleTelemedicinaEditar = PERMISSOES.medicos.telemedicina.editar;

    rows = signal<TelemedicinaRow[]>([]);

    formData: TelemedicinaFormData = {
        tipo: '',
        dataInicio: undefined,
        dataFinal: undefined
    };

    tipos = signal<TipoTelemedicinaResponse[]>([]);

    displayEncerrarDialog = false;
    selectedTipo = '';
    selectedDataInicio = '';
    selectedRowIndex: number | null = null;
    encerrarDataFim: Date | null = null;

    statusOptions = [
        { name: 'Ativas', value: 'ativas' },
        { name: 'Histórico', value: 'inativas' }
    ];
    selectedStatus: 'ativas' | 'inativas' = 'ativas';

    loading = signal(false);
    erro = signal<string | undefined>(undefined);
    displayDialog = false;

    selectedTab: string = 'ATIVAS';
    hashableOptionsTab: Map<string, TabContainerOptionType> = new Map([
        [
            'ATIVAS',
            {
                label: 'Telemedicina Ativas',
                value: 'ATIVAS',
                icon: 'pi pi-video'
            }
        ],
        ['HISTORICO', { label: 'Histórico de Telemedicina', value: 'HISTORICO', icon: 'pi pi-history' }]
    ]);
    optionsTab: TabContainerOptionType[] = Array.from(this.hashableOptionsTab.values());

    selectionAtiva: TelemedicinaRow | null = null;
    selectionAtivaIndex: number = -1;
    metadata: TableMetadata = {
        headers: {
            tipo: { type: 'text', hidden: false, default: '', value: 'Tipo Telemedicina' },
            dataInicio: { type: 'text', hidden: false, default: '', value: 'Data Início' },
            dataFim: { type: 'text', hidden: false, default: '', value: 'Data Fim' }
        },
        title: 'tipo'
    };
    tableColumnOrder = ['tipo', 'dataInicio', 'dataFim'];

    obterTipoExibicao(item: TelemedicinaRow): string {
        const t = item.tipo || '';
        return t.length > 70 ? t.substring(0, 70) + '...' : t;
    }

    obterStatusLabel(item: TelemedicinaRow): string {
        return item.dataFim ? 'ENCERRADA' : 'ATIVA';
    }

    obterStatusColor(item: TelemedicinaRow): string {
        return item.dataFim ? 'var(--p-red-500)' : 'var(--p-green-500)';
    }

    constructor(
        private feature: TelemedicinaFeatureService,
        private messageService: MessageService
    ) {
        this.atualizarAcoesAtivas(false);
    }

    ngOnInit(): void {
        const p = this.pessoa();
        // Carregar tipos de telemedicina para o diálogo
        this.feature.listarTiposTelemedicina().subscribe({
            next: (lista) => this.tipos.set(lista || []),
            error: (err: Error) => this.logger.error('Erro ao listar tipos de telemedicina', err)
        });
        if (p && p.id != null && !!p.uf) {
            this.carregar(p);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p && p.id != null && !!p.uf) {
                this.carregar(p);
            } else {
                this.rows.set([]);
                this.limparSelecaoAtivas();
            }
        }
    }

    // Tab change handling
    onTabChanged(opt: { label: string; value: string }): void {
        this.selectedTab = opt.value;
        this.selectedStatus = opt.value === 'ATIVAS' ? 'ativas' : 'inativas';
        this.limparSelecaoAtivas();
        this.onToggleStatus();
    }

    onToggleStatus(): void {
        const p = this.pessoa();
        if (p && p.id != null && !!p.uf) this.carregar(p);
    }

    private carregar(pessoa: Pessoa): void {
        this.loading.set(true);
        this.erro.set(undefined);
        const obs = this.selectedStatus === 'ativas' ? this.feature.buscar({ id: pessoa.id, uf: pessoa.uf }) : this.feature.buscarHistorico({ id: pessoa.id, uf: pessoa.uf });
        obs.subscribe({
            next: (resp: MedicoTelemedicinaResponse[] | MedicoTelemedicinaHistoricoResponse[]) => {
                const linhas = (resp ?? []) as Array<{ nmTipoTelemedicina?: string; dtInicio?: string | null; dtFim?: string | null }>;
                this.rows.set(
                    linhas.map((r) => ({
                        tipo: r.nmTipoTelemedicina ?? '',
                        dataInicio: this.formatarData(r.dtInicio),
                        dataFim: this.formatarData(r.dtFim)
                    }))
                );
                this.limparSelecaoAtivas();
            },
            error: (err: Error) => {
                this.logger.error('Erro ao buscar telemedicina', err);
                this.erro.set(err.message || 'Falha ao carregar informações de telemedicina.');
                this.rows.set([]);
                this.limparSelecaoAtivas();
            }
        }).add(() => this.loading.set(false));
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor ?? '');
        }
    }

    openDialog(): void {
        this.displayDialog = true;
    }

    closeDialog(): void {
        this.displayDialog = false;
    }

    onRowClickAtivas(selected: TelemedicinaRow | null): void {
        this.selectionAtiva = selected;
        this.selectionAtivaIndex = selected ? this.rows().findIndex((r) => this.isEqualTelemedicinaRow(r, selected)) : -1;
        this.atualizarAcoesAtivas(!!selected);
    }

    private isEqualTelemedicinaRow(a: TelemedicinaRow, b: TelemedicinaRow): boolean {
        return a.tipo === b.tipo && a.dataInicio === b.dataInicio && a.dataFim === b.dataFim;
    }

    private abrirRemoverSelecionado(): void {
        if (!this.selectionAtiva) return;
        const idx = this.rows().findIndex((r) => this.isEqualTelemedicinaRow(r, this.selectionAtiva as TelemedicinaRow));
        if (idx < 0) return;
        this.openEncerrar(this.selectionAtiva, idx);
    }

    private atualizarAcoesAtivas(temSelecao: boolean): void {
        const opcaoAtivas = this.hashableOptionsTab.get('ATIVAS');
        if (!opcaoAtivas) return;

        const items: MenuItem[] = [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                styleClass: 'bg-green-500',
                command: () => this.openDialog()
            },
            {
                label: 'Remover',
                icon: 'pi pi-trash',
                styleClass: 'bg-red-500',
                disabled: !temSelecao,
                command: () => this.abrirRemoverSelecionado()
            }
        ];

        opcaoAtivas.actions = { items };
        this.hashableOptionsTab.set('ATIVAS', opcaoAtivas);
        this.optionsTab = Array.from(this.hashableOptionsTab.values());
    }

    private limparSelecaoAtivas(): void {
        this.selectionAtiva = null;
        this.selectionAtivaIndex = -1;
        this.atualizarAcoesAtivas(false);
    }

    // Eventos do diálogo externo
    onDialogSaved(data: TelemedicinaDialogFormData): void {
        // Integração: inserir novo registro de telemedicina
        const p = this.pessoa();
        if (!p || p.id == null || !p.uf) {
            this.logger.error('Pessoa não informada para inserção de telemedicina');
            return;
        }

        const tipoObj = this.tipos().find((t) => t.nmTipoTelemedicina === data.tipo);
        const body: MedicoTelemedicinaResponse = {
            idSolicitante: p.id,
            sgUf: p.uf,
            idTipoTelemedicina: tipoObj?.idTipoTelemedicina,
            nmTipoTelemedicina: data.tipo,
            dtInicio: this.toIsoDateString(data.dataInicio ?? null) || null,
            dtFim: this.toIsoDateString(data.dataFinal ?? null) || null
        };

        this.feature.inserirTelemedicina(body).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Telemedicina salva com sucesso.'
                });
                // Recarrega a lista atual (ativas)
                this.formData = { ...data };
                const pessoa = this.pessoa();
                if (pessoa && pessoa.id != null && !!pessoa.uf) {
                    this.carregar(pessoa);
                }
            },
            error: (err: Error) => {
                this.logger.error('Falha ao inserir telemedicina', err);
            }
        });
    }

    onDialogCanceled(): void {
        // Nenhuma ação específica além de fechar; mantido para futuras regras
    }

    // Ações para Encerrar (definir Data Fim)
    openEncerrar(row: TelemedicinaRow, index: number): void {
        this.selectedTipo = row.tipo;
        this.selectedDataInicio = row.dataInicio ?? '';
        this.selectedRowIndex = index;
        this.encerrarDataFim = null;
        this.displayEncerrarDialog = true;
    }

    onEncerrarSaved(isoDate: string): void {
        // Integração: atualizar registro ativo (definir dtFim)
        const p = this.pessoa();
        if (!p || p.id == null || !p.uf) {
            this.logger.error('Pessoa não informada para atualização de telemedicina');
            return;
        }

        const tipoObj = this.tipos().find((t) => t.nmTipoTelemedicina === this.selectedTipo);
        const body: MedicoTelemedicinaResponse = {
            idSolicitante: p.id,
            sgUf: p.uf,
            idTipoTelemedicina: tipoObj?.idTipoTelemedicina,
            nmTipoTelemedicina: this.selectedTipo,
            dtInicio: this.toIsoDateString(this.selectedDataInicio) || null,
            dtFim: isoDate || null
        };

        this.feature
            .atualizarTelemedicina(body)
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Telemedicina salva com sucesso.'
                    });
                    const pessoa = this.pessoa();
                    if (pessoa && pessoa.id != null && !!pessoa.uf) {
                        this.carregar(pessoa);
                    }
                },
                error: (err: Error) => {
                    this.logger.error('Falha ao atualizar telemedicina', err);
                }
            })
            .add(() => this.onEncerrarCanceled());
    }

    onEncerrarCanceled(): void {
        this.displayEncerrarDialog = false;
        this.encerrarDataFim = null;
        this.selectedRowIndex = null;
        this.selectedDataInicio = '';
    }

    private toIsoDateString(value: Date | string | null | undefined): string {
        if (!value) return '';
        try {
            const normalizedValue = typeof value === 'string' ? value.trim() : value;
            if (typeof normalizedValue === 'string') {
                const brDateMatch = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (brDateMatch) {
                    const [, dd, mm, yyyy] = brDateMatch;
                    return `${yyyy}-${mm}-${dd}`;
                }
            }

            const d = typeof normalizedValue === 'string' ? new Date(normalizedValue) : normalizedValue;
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return String(value);
        }
    }
}
